import { createSlice } from "@reduxjs/toolkit";

const ALIAS = { GHC: "GHS" };
const up = (v) => String(v || "").toUpperCase();
const norm = (c) => ALIAS[up(c)] || up(c);

// Existing schemas (keep)
export const CROSS_BORDER_FIELD_SCHEMAS = {
    USD: [
        { key: "accountNumber", label: "Account number", required: true },
        { key: "routingNumber", label: "Routing number", required: true },
        { key: "accountName", label: "Account holder's name", required: true },
    ],
    CAD: [
        { key: "accountNumber", label: "Account number", required: true },
        { key: "transitNumber", label: "Transit number", required: true },
        { key: "institutionNumber", label: "Institution number", required: true },
        { key: "accountName", label: "Account holder's name", required: true },
    ],
    GBP: [
        { key: "iban", label: "IBAN / Account number", required: true },
        { key: "swiftBic", label: "SWIFT / BIC", required: true },
        { key: "sortCode", label: "Sort code", required: true },
        { key: "accountName", label: "Account holder's name", required: true },
    ],
    EUR: [
        { key: "iban", label: "IBAN / Account number", required: true },
        { key: "swiftBic", label: "SWIFT / BIC", required: true },
        { key: "accountName", label: "Account holder's name", required: true },
    ],
    NGN: [
        { key: "accountNumber", label: "Account number", required: true },
        { key: "accountName", label: "Account name", required: true },
        { key: "bankCode", label: "Bank code", required: false },
        { key: "bankName", label: "Bank", required: false },
    ],
    KES: [
        { key: "accountNumber", label: "Account number", required: true },
        { key: "bankCode", label: "Bank code", required: true },
        { key: "branchCode", label: "Branch code", required: true },
        { key: "accountName", label: "Account holder's name", required: true },
    ],
    GHS: [
        { key: "accountNumber", label: "Account number", required: true },
        { key: "bankCode", label: "Bank code", required: true },
        { key: "accountName", label: "Account holder's name", required: true },
        { key: "swiftBic", label: "SWIFT / BIC", required: false },
    ],
};

const emptyFieldsFor = (currency) =>
    (CROSS_BORDER_FIELD_SCHEMAS[norm(currency)] || []).reduce((o, f) => {
        o[f.key] = "";
        return o;
    }, {});

const USD_RECIPIENT_DEFAULT = {
    bank_name: "",
    account_type: "us", // 'us' | 'iban' | 'international_wire'
    paymentRail: "ach", // 'ach' | 'rtp' | 'wire' | 'swift'
    account_owner_type: "individual", // 'individual' | 'corporate'
    account_owner_name: "",
    first_name: "",
    last_name: "",
    business_name: "",
    address: {
        street_line_1: "",
        street_line_2: "",
        city: "",
        state: "",
        subdivision: "",
        postal_code: "",
        country: "US",
    },
    bank_address: {
        street_line_1: "",
        street_line_2: "",
        city: "",
        state: "",
        postal_code: "",
        country: "US",
    },
    relationship: "",
    email: "",
    account: {
        account_number: "",
        routing_number: "",
        checking_or_savings: "",
    },
    iban: {
        account_number: "",
        bic: "",
        country: "",
    },
    swift: {
        account_number: "",
        bic: "",
        country: "",
    },
};

const initialState = {
    fromCurrency: (typeof localStorage !== "undefined" && localStorage.getItem("default")) || "NGN",
    amount: "",
    pin: "",
    fee: 0,
    rate: null,
    rateKey: "",
    isOTC: false,
    rateLoading: false,
    rateError: null,

    memo: "",
    invoice: null,
    otherDocument: null,
    provider: null,

    recipient: {
        country: "",
        bankName: "",
        toCurrency: "",
        beneficiaryId: "",
        transactionId: "",
        fields: {},
        usd: { ...USD_RECIPIENT_DEFAULT },
    },
};

const crossBorderTransferSlice = createSlice({
    name: "crossBorderTransfer",
    initialState,
    reducers: {
        setCrossBorderFromCurrency: (state, action) => {
            state.fromCurrency = norm(action.payload);
        },

        setCrossBorderToCurrency: (state, action) => {
            const to = norm(action.payload);
            state.recipient.toCurrency = to;

            state.rate = null;
            state.rateKey = "";
            state.isOTC = false;
            state.rateError = null;
            state.recipient.bankName = "";
            if (to === "USD") {
                state.recipient.fields = {};
                state.recipient.usd = { ...USD_RECIPIENT_DEFAULT };
            } else {
                state.recipient.fields = emptyFieldsFor(to);
                state.recipient.usd = { ...USD_RECIPIENT_DEFAULT };
            }
        },

        setCrossBorderAmount: (state, action) => {
            state.amount = String(action.payload || "");
        },
        setCrossBorderPin: (state, action) => {
            state.pin = String(action.payload || "");
        },
        setCrossBorderFee: (state, action) => {
            state.fee = Number(action.payload) || 0;
        },

        setCrossBorderRateStart: (state) => {
            state.rateLoading = true;
            state.rateError = null;
            state.rateKey = "";
        },
        setCrossBorderRateSuccess: (state, action) => {
            const p = action.payload || {};
            state.rateLoading = false;
            state.rateError = null;

            state.rate =
                Number(p.roundedRate ?? p.rate ?? p.value ?? (p.data && p.data.roundedRate)) || 0;

            const rk =
                p.rateKey ??
                p.ratekey ??
                p.key ??
                (p.data && (p.data.rateKey || p.data.ratekey || p.data.key));

            state.rateKey = rk != null ? String(rk).trim() : "";
            state.isOTC = !!p.isOTC;

            if (p.fromCurrency) state.fromCurrency = String(p.fromCurrency).toUpperCase();
            if (p.toCurrency) state.recipient.toCurrency = String(p.toCurrency).toUpperCase();
        },
        setCrossBorderRateError: (state, action) => {
            state.rateLoading = false;
            state.rateError = action.payload || "Failed to fetch FX rate";
            state.rate = null;
            state.rateKey = "";
        },

        setCrossBorderRecipientMeta: (state, action) => {
            const { bankName, country } = action.payload || {};
            if (typeof bankName !== "undefined") state.recipient.bankName = bankName;
            if (typeof country !== "undefined") state.recipient.country = country;
            if (typeof bankName !== "undefined") state.recipient.usd.bank_name = bankName;
        },

        setCrossBorderRecipientField: (state, action) => {
            const { key, value } = action.payload || {};
            state.recipient.fields[key] = value;
        },

        setCrossBorderUsdAll: (state, action) => {
            const p = action.payload || {};
            const u = state.recipient.usd;
            const bankName = p.bank_name ?? p.bankName;
            const accountType = p.account_type ?? p.accountType;
            const paymentRail = p.payment_rail ?? p.paymentRail;
            const ownerType = p.account_owner_type ?? p.accountOwnerType;
            const ownerName = p.account_owner_name ?? p.accountOwnerName;

            if (bankName !== undefined) {
                u.bank_name = bankName;
                state.recipient.bankName = bankName;
            }
            if (accountType !== undefined) u.account_type = accountType;
            if (paymentRail !== undefined) u.paymentRail = paymentRail;
            if (ownerType !== undefined) u.account_owner_type = ownerType;
            if (ownerName !== undefined) u.account_owner_name = ownerName;

            if (p.first_name !== undefined) u.first_name = p.first_name;
            if (p.last_name !== undefined) u.last_name = p.last_name;
            if (p.business_name !== undefined) u.business_name = p.business_name;
            if (p.relationship !== undefined) u.relationship = p.relationship;
            if (p.email !== undefined) u.email = p.email;

            // camelCase identity support
            if (p.firstName !== undefined) u.first_name = p.firstName;
            if (p.lastName !== undefined) u.last_name = p.lastName;
            if (p.businessName !== undefined) u.business_name = p.businessName;

            // method blocks (deep merge)
            if (p.account) u.account = { ...u.account, ...p.account };
            if (p.iban) u.iban = { ...u.iban, ...p.iban };
            if (p.swift) u.swift = { ...u.swift, ...p.swift };
            if (p.address) u.address = { ...u.address, ...p.address };
            if (p.bank_address) u.bank_address = { ...u.bank_address, ...p.bank_address };
        },

        setCrossBorderProvider: (state, action) => {
            state.provider = action.payload;
        },
        setCrossBorderMemo: (state, action) => {
            state.memo = String(action.payload || "");
        },
        setCrossBorderInvoice: (state, action) => {
            const p = action.payload || {};
            state.invoice = p?.url ? { url: p.url, name: p.name || "" } : null;
        },
        setCrossBorderOtherDocument: (state, action) => {
            const p = action.payload || {};
            state.otherDocument = p?.url ? { url: p.url, name: p.name || "" } : null;
        },

        patchCrossBorderTransfer: (state, action) => {
            const patch = action.payload || {};
            const { recipient: recipientPatch, ...rootPatch } = patch;
            Object.assign(state, rootPatch);

            if (recipientPatch) {
                const currentRecipient = state.recipient || initialState.recipient;
                const currentUsd = currentRecipient.usd || USD_RECIPIENT_DEFAULT;
                const usdPatch = recipientPatch.usd || {};

                state.recipient = {
                    ...currentRecipient,
                    ...recipientPatch,
                    fields: {
                        ...(currentRecipient.fields || {}),
                        ...(recipientPatch.fields || {}),
                    },
                    usd: {
                        ...currentUsd,
                        ...usdPatch,
                        account: {
                            ...(currentUsd.account || {}),
                            ...(usdPatch.account || {}),
                        },
                        iban: {
                            ...(currentUsd.iban || {}),
                            ...(usdPatch.iban || {}),
                        },
                        swift: {
                            ...(currentUsd.swift || {}),
                            ...(usdPatch.swift || {}),
                        },
                        address: {
                            ...(currentUsd.address || {}),
                            ...(usdPatch.address || {}),
                        },
                        bank_address: {
                            ...(currentUsd.bank_address || {}),
                            ...(usdPatch.bank_address || {}),
                        },
                    },
                };
            }
        },

        resetCrossBorderTransfer: () => initialState,
    },
});

export const {
    setCrossBorderFromCurrency,
    setCrossBorderToCurrency,
    setCrossBorderAmount,
    setCrossBorderPin,
    setCrossBorderFee,
    setCrossBorderRateStart,
    setCrossBorderRateSuccess,
    setCrossBorderRateError,
    setCrossBorderRecipientMeta,
    setCrossBorderRecipientField,
    setCrossBorderUsdAll,
    setCrossBorderProvider,
    setCrossBorderMemo,
    setCrossBorderInvoice,
    setCrossBorderOtherDocument,
    patchCrossBorderTransfer,
    resetCrossBorderTransfer,
} = crossBorderTransferSlice.actions;

export default crossBorderTransferSlice.reducer;

export const selectCrossBorderTransfer = (state) => state.crossBorderTransfer || initialState;

export const selectCrossBorderSchema = (state) => {
    const to = norm(
        (state.crossBorderTransfer && state.crossBorderTransfer.recipient.toCurrency) || ""
    );
    return CROSS_BORDER_FIELD_SCHEMAS[to] || [];
};

export const selectCrossBorderPayload = (state) => {
    const s = selectCrossBorderTransfer(state);
    const to = norm(s.recipient.toCurrency);

    const base = {
        amount: s.amount,
        fromCurrency: norm(s.fromCurrency),
        toCurrency: to,
        fee: s.fee,
        rate: s.rate,
        rateKey: s.rateKey,
        isOTC: s.isOTC,
        memo: s.memo,
        invoice: s.invoice,
        otherDocument: s.otherDocument,
    };

    if (to === "USD") {
        const u = s.recipient.usd || USD_RECIPIENT_DEFAULT;

        const normalizedType =
            u.account_type === "swift" ? "international_wire" : u.account_type || "us";
        const recipient = {
            country: s.recipient.country,
            bankName: s.recipient.bankName || u.bank_name || "",
            bank_name: u.bank_name || s.recipient.bankName || "",
            beneficiaryId: s.recipient.beneficiaryId || "",
            transactionId: s.recipient.transactionId || "",

            account_owner_type: u.account_owner_type || "individual",
            account_owner_name: u.account_owner_name || "",
            first_name: u.first_name || "",
            last_name: u.last_name || "",
            business_name: u.business_name || "",

            account_type: normalizedType,
            address: u.address || {},
            relationship: u.relationship || "",
            email: u.email || "",
            ...(normalizedType === "us"
                ? {
                      bank_address: u.bank_address || {},
                  }
                : {}),
        };

        // Attach only relevant method block
        if (recipient.account_type === "us") recipient.account = u.account || {};
        if (recipient.account_type === "iban") recipient.iban = u.iban || {};
        if (
            recipient.account_type === "international_wire" ||
            recipient.account_type === "swift"
        ) {
            recipient.swift = u.swift || {};
        }

        return { ...base, recipient };
    }

    // Non-USD: original behavior
    return {
        ...base,
        recipient: {
            bankName: s.recipient.bankName,
            country: s.recipient.country,
            beneficiaryId: s.recipient.beneficiaryId || "",
            transactionId: s.recipient.transactionId || "",
            ...s.recipient.fields,
        },
    };
};
